import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  UpdateSupportTicketCategoryDtoCategoryEnum,
  UpdateSupportTicketPriorityDtoPriorityEnum,
} from '../../../../libs/api-client/src/generated/models';
import {
  addTicketNote,
  downloadAttachment,
  fetchSupportAgents,
  fetchSupportTicket,
  fetchTicketShopState,
  isTicketConflict,
  replyToTicket,
  setTicketAssignee,
  setTicketCategory,
  setTicketPriority,
  setTicketStatus,
  toReplyStatus,
  toTicketCategory,
  toTicketPriority,
  toTicketStatus,
} from '../lib/support-api';
import { supportContextLabelKey } from '../lib/support-context-fields';
import { clearDraft, readDraft, writeDraft } from '../lib/support-draft';
import {
  supportContextValueGroup,
  supportEnumLabel,
  supportEventValueGroup,
  type Translate,
} from '../lib/support-enum-labels';
import { historyDetail, reviewAskState } from '../lib/support-review-ask';
import { describeRemoteChanges } from '../lib/support-ticket-freshness';
import { documentLookupPath } from '../lib/document-lookup';
import { shopFilePath } from '../lib/shop-file';
import { PageBody, PageHead } from '../components/Page';
import { StatusBanner } from '../components/StatusBanner';
import { CannedResponsePicker } from '../components/CannedResponsePicker';
import {
  draftLanguage,
  insertIntoDraft,
  type SnippetLanguage,
} from '../lib/canned-responses';
import { Icon } from '../components/Brand';
import { formatLanguageName, intlLocale } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';
import type { SupportedLocale } from '../i18n';

const PRIORITIES = Object.values(UpdateSupportTicketPriorityDtoPriorityEnum);
const CATEGORIES = Object.values(UpdateSupportTicketCategoryDtoCategoryEnum);

/**
 * Formats a timestamp the way an agent reads one: date plus minute, in the chosen language.
 *
 * The locale is passed rather than left as `undefined`: the browser's own locale was what
 * decided this before, so a German operator who had switched the page to English still got
 * `7. Sept. 2026` next to English copy.
 *
 * Tolerates a missing value rather than throwing — several of these come off optional
 * fields on the generated models, and a ticket page that will not render because one
 * history row lacks a date is a worse outcome than an em dash.
 */
function ts(value: string | undefined | null, locale: SupportedLocale): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(intlLocale(locale), {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/**
 * Renders one snapshot value, in the reader's language.
 *
 * `null` prints as "unknown", never as 0 or "no" — the snapshot builder returns nulls when it
 * could not read the shop at all, and an agent has to be able to trust that `Invoices: 0`
 * means the merchant has issued nothing rather than that a count query timed out.
 *
 * The snapshot is a `Json` column, so everything in it arrives as `unknown` and this used to end
 * at `String(value)` — which put `PROFESSIONAL`, `ACTIVE` and `KLEINUNTERNEHMEN` next to German
 * labels, and an ISO timestamp next to four formatted dates on the same page. The FIELD decides
 * which of those a value is; nothing about its type survives the round trip. A field this does
 * not recognise still falls through to `String(value)`: a snapshot the API grows must show
 * something rather than nothing.
 *
 * Takes `t` and the locale rather than reading them itself: it is called from both sidebar
 * panels and this keeps it a pure function of its arguments, which is what lets one spec cover
 * it.
 */
function snapshotValue(
  field: string,
  value: unknown,
  t: Translate,
  locale: SupportedLocale,
): string {
  if (value === null || value === undefined) return t('support.ticket.unknown');
  if (typeof value === 'boolean') {
    return t(value ? 'support.ticket.yes' : 'support.ticket.no');
  }
  if (field === 'capturedAt' && typeof value === 'string') {
    return ts(value, locale);
  }
  const group = supportContextValueGroup(field);
  if (group && typeof value === 'string') {
    return supportEnumLabel(t, group, value);
  }
  return String(value);
}

/** A sidebar panel: heading plus a definition list. */
function SidePanel({
  title,
  children,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
}) {
  return (
    <div className="surface p-4 text-sm">
      <h2 className="text-gray-900 font-semibold mb-3">{title}</h2>
      {children}
    </div>
  );
}

/** One `dt`/`dd` row inside a {@link SidePanel}. */
function Row({
  label,
  children,
  tone,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
  readonly tone?: string;
}) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-gray-500">{label}</dt>
      <dd className={`text-right break-all ${tone ?? 'text-gray-900'}`}>
        {children}
      </dd>
    </div>
  );
}

/**
 * One ticket, as the support team works it.
 *
 * The layout puts the thread and the reply box first and everything else in a sidebar,
 * because the answer is the job. The public/internal toggle sits ON the send button rather
 * than in a menu: choosing wrongly is the one mistake here that cannot be taken back, so
 * it must be visible at the moment of sending, not two clicks earlier.
 */
export function SupportTicketPage() {
  const { t, locale } = useLocale();
  const { id = '' } = useParams();
  const queryClient = useQueryClient();

  /**
   * The unsent draft this page was opened with, if there was one.
   *
   * Read once, in a lazy initialiser, so the composer starts life holding it rather than
   * mounting empty and being filled by an effect a frame later — an agent who starts typing
   * into that frame would have their first keystrokes overwritten.
   */
  const [restoredDraft] = useState(() => readDraft(id));

  const [body, setBody] = useState(restoredDraft?.body ?? '');
  const [isInternal, setIsInternal] = useState(
    restoredDraft?.isInternal ?? false,
  );
  const [nextStatus, setNextStatus] = useState('');
  /**
   * The snippet last inserted into the composer, for the one-line account under the picker. Shown
   * only while the composer still holds exactly what the insert produced, like the restored-draft
   * note: once the agent edits, the note would describe text that is no longer there.
   */
  const [insertedSnippet, setInsertedSnippet] = useState<{
    readonly title: string;
    readonly language: SnippetLanguage;
    readonly body: string;
  } | null>(null);
  /**
   * Whether the keyboard is inside the sidebar's controls right now.
   *
   * A focused `<select>` is not "editing" by any state we hold — the agent has opened it and
   * not chosen yet — but swapping its bound value at that moment is exactly the interruption
   * this page must not cause. Tracked with capture-phase focus events because focus is not
   * React state and `document.activeElement` would not re-render anything.
   */
  const [panelFocused, setPanelFocused] = useState(false);

  /** Attachment id currently being fetched, and the last failure, for the thread's file list. */
  const [downloadingAttachment, setDownloadingAttachment] = useState<
    string | null
  >(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  /**
   * The ticket ON SCREEN.
   *
   * Never refetched behind the agent's back: no interval, no focus refetch. It changes on
   * mount, after one of this page's own mutations, and when the agent takes an update that
   * the poll below found. Everything rendered from server state — the four sidebar selects
   * above all — is therefore stable for as long as someone is working in it.
   */
  const ticketQuery = useQuery({
    queryKey: ['support-ticket', id],
    queryFn: () => fetchSupportTicket(id),
    enabled: id.length > 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  /**
   * The ticket ON THE SERVER, polled.
   *
   * Same endpoint, separate cache entry, and deliberately never rendered as form state. Its
   * only job is to answer "did someone else touch this?" — which is the question the queue's
   * assignee column can only answer for a page you are not currently on.
   *
   * `refetchIntervalInBackground` stays at its default of `false`: a ticket page left open on
   * a second monitor should not poll all afternoon.
   */
  const pulseQuery = useQuery({
    queryKey: ['support-ticket', id, 'pulse'],
    queryFn: () => fetchSupportTicket(id),
    enabled: id.length > 0 && ticketQuery.isSuccess,
    refetchInterval: 20_000,
    staleTime: 0,
  });

  const agentsQuery = useQuery({
    queryKey: ['support-agents'],
    queryFn: fetchSupportAgents,
    staleTime: 5 * 60_000,
  });

  /**
   * The shop as it stands right now.
   *
   * Polled, unlike the ticket itself, and that is not an inconsistency: nothing here is bound
   * to a control, so a repaint can only ever change text an agent is reading — the reason the
   * ticket query holds still is that four `<select>`s hang off it. A minute is chosen against
   * what this panel is for: an agent watching whether the merchant's fix took effect.
   */
  const shopStateQuery = useQuery({
    queryKey: ['support-ticket', id, 'shop-state'],
    queryFn: () => fetchTicketShopState(id),
    enabled: id.length > 0 && ticketQuery.isSuccess,
    refetchInterval: 60_000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['support-ticket', id] });
    queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
  };

  const sendMutation = useMutation({
    mutationFn: () => {
      // The version this message was written against travels WITH it. The API compares it to
      // the row as it stands at that instant and answers 409 rather than sending — which is
      // the only place the question can be answered without a gap, since a poll is only ever
      // as fresh as its last tick and a reply takes minutes to write.
      const writtenAgainst = ticketQuery.data?.stateToken;
      return isInternal
        ? addTicketNote(id, body, writtenAgainst)
        : replyToTicket(id, body, toReplyStatus(nextStatus), writtenAgainst);
    },
    onSuccess: () => {
      setBody('');
      setNextStatus('');
      // Explicit rather than left to the persistence effect below: what makes a draft safe to
      // throw away is that it has been SENT, and saying so here is what keeps that true if the
      // effect's dependencies ever change.
      clearDraft(id);
      invalidate();
    },
    onError: (error) => {
      // A 409 means something changed that this page has not seen yet. Pull it so the banner
      // can name it, instead of leaving the agent with a refusal and no account of why.
      if (isTicketConflict(error)) {
        queryClient.invalidateQueries({
          queryKey: ['support-ticket', id, 'pulse'],
        });
      }
    },
  });

  /**
   * Whether resolving this ticket should also schedule the App Store review invitation.
   *
   * Opt-IN and reset after every status change: an agent who ticked it for one ticket must not
   * carry that decision into the next one they resolve from the same page.
   */
  const [requestReviewAsk, setRequestReviewAsk] = useState(false);

  const statusMutation = useMutation({
    mutationFn: (status: string) => {
      const narrowed = toTicketStatus(status);
      if (!narrowed) throw new Error(`Unknown status: ${status}`);
      // The flag is only meaningful on the resolving transition; the API ignores it otherwise,
      // and sending `false` for every other move keeps that explicit on this side too.
      return setTicketStatus(
        id,
        narrowed,
        narrowed === 'RESOLVED' && requestReviewAsk,
      );
    },
    onSuccess: () => {
      setRequestReviewAsk(false);
      invalidate();
    },
  });

  const priorityMutation = useMutation({
    mutationFn: (priority: string) => {
      const narrowed = toTicketPriority(priority);
      if (!narrowed) throw new Error(`Unknown priority: ${priority}`);
      return setTicketPriority(id, narrowed);
    },
    onSuccess: invalidate,
  });

  // Re-filing a ticket the merchant put in the wrong category is the commonest hygiene
  // action after assignment; the endpoint existed with no way to call it.
  const categoryMutation = useMutation({
    mutationFn: (category: string) => {
      const narrowed = toTicketCategory(category);
      if (!narrowed) throw new Error(`Unknown category: ${category}`);
      return setTicketCategory(id, narrowed);
    },
    onSuccess: invalidate,
  });

  const assigneeMutation = useMutation({
    mutationFn: (assignee: string) =>
      setTicketAssignee(id, assignee === '' ? null : assignee),
    onSuccess: invalidate,
  });

  /**
   * Whether taking an update right now would interrupt someone.
   *
   * Erring towards "yes" on purpose. A half-written reply is minutes of work and the one
   * thing on this page that cannot be recovered; a banner shown for a few seconds longer
   * than strictly necessary costs nothing.
   */
  const isEditing =
    body.trim().length > 0 ||
    nextStatus !== '' ||
    requestReviewAsk ||
    panelFocused ||
    sendMutation.isPending ||
    statusMutation.isPending ||
    priorityMutation.isPending ||
    categoryMutation.isPending ||
    assigneeMutation.isPending;

  /**
   * The catalogue label for a snapshot field, falling back to its raw name.
   *
   * One helper rather than the lookup inline in both panels: the frozen panel and the live one
   * must label the same field identically or the comparison between them reads as two
   * different facts.
   */
  const contextLabel = (field: string): string => {
    const labelKey = supportContextLabelKey(field);
    return labelKey ? t(labelKey) : field;
  };

  const shopState = shopStateQuery.data;
  /** Snapshot keys the API reports as having moved since the ticket was opened. */
  const changedKeys = new Set(shopState?.changedKeys ?? []);

  const latest = pulseQuery.data;
  const remoteChanges = describeRemoteChanges(ticketQuery.data, latest, t);
  const hasRemoteChanges = remoteChanges.length > 0;

  /** Replaces the rendered ticket with the polled one. No refetch — the data is already here. */
  const showLatest = () => {
    if (!latest) return;
    queryClient.setQueryData(['support-ticket', id], latest);
    // Clears a `TicketMovedError` from a blocked send. Taking the update IS the acknowledgement
    // the block was waiting for, so leaving the refusal on screen afterwards would only read as
    // a second, unrelated failure.
    sendMutation.reset();
    // The chosen post-reply status may not be legal on the ticket as it now stands, and a
    // `<select>` holding a value its options no longer contain renders blank.
    setNextStatus((chosen) =>
      chosen && !latest.allowedNextStatuses.includes(chosen) ? '' : chosen,
    );
  };

  /**
   * Persists the composer on every keystroke.
   *
   * Written straight through rather than debounced: the failure this exists to survive is a
   * reload or a crash, and a debounce is precisely a window in which the most recent words are
   * not yet saved. A few KB into `localStorage` per keystroke is not a cost worth that.
   */
  useEffect(() => {
    writeDraft(id, { body, isInternal });
  }, [id, body, isInternal]);

  /**
   * Adds a canned response to the reply (work order SUPPORT-TOOLS AC-5.3).
   *
   * The snippet becomes DRAFT TEXT, not a separate attachment to the reply: it lands in the same
   * `body` the agent types into, and is written through to the per-ticket draft here and now
   * rather than left to the persistence effect above — what makes it survive a reload is that it
   * is in `localStorage`, and saying so at the insert keeps that true whatever the effect does.
   * The picker already chose the ticket's language and filled the placeholders (AC-5.2).
   */
  const handleInsertSnippet = (
    text: string,
    title: string,
    language: SnippetLanguage,
  ) => {
    const next = insertIntoDraft(body, text);
    setBody(next);
    writeDraft(id, { body: next, isInternal });
    setInsertedSnippet({ title, language, body: next });
  };

  /**
   * Fetches one attachment and hands it to the browser as a download.
   *
   * Through an authenticated request rather than an anchor, because the agent route is behind
   * the operator token and an `<a href>` carries no Authorization header.
   */
  const handleAttachmentDownload = (
    attachmentId: string,
    fileName: string,
  ): void => {
    if (downloadingAttachment) return;
    setDownloadingAttachment(attachmentId);
    setAttachmentError(null);

    downloadAttachment(attachmentId)
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        link.remove();
        // Revoked on the next macrotask, not in this one. Firefox and Safari start reading
        // the blob AFTER the click handler returns, so revoking synchronously cancels the
        // download that was just triggered — the agent gets nothing and no error.
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      })
      .catch(() => setAttachmentError(fileName))
      .finally(() => setDownloadingAttachment(null));
  };

  /**
   * An idle page takes updates silently; a page being worked in asks first.
   *
   * This is the whole compromise: the poll is always running, but the swap only happens when
   * there is nothing on screen to lose.
   */
  useEffect(() => {
    if (!latest || isEditing) return;
    // The server's own version marker, not a comparison invented here: the API decides what
    // counts as changed, and a second opinion on this side could only ever drift from it.
    if (ticketQuery.data?.stateToken === latest.stateToken) return;
    queryClient.setQueryData(['support-ticket', id], latest);
  }, [latest, isEditing, ticketQuery.data, queryClient, id]);

  if (ticketQuery.isLoading) {
    return <div className="p-8 text-gray-500">{t('common.loading')}</div>;
  }
  if (ticketQuery.isError || !ticketQuery.data) {
    return (
      <div className="p-4 sm:p-6">
        <StatusBanner tone="error">
          {t('support.ticket.loadError')}
        </StatusBanner>
      </div>
    );
  }

  const ticket = ticketQuery.data;
  /** Where the post-resolution review invitation stands, or null if none was ever asked for. */
  const askState = reviewAskState(ticket.reviewAsk, ticket.events);

  return (
    <>
      <PageHead
        eyebrow={ticket.reference}
        title={ticket.subject}
        lead={
          <>
            {ticket.shopDomain} ·{' '}
            {supportEnumLabel(t, 'category', ticket.category)}
            {ticket.overdue ? (
              <span className="text-red-600 font-medium">
                {' '}
                · {t('support.ticket.overdue')}
              </span>
            ) : null}
          </>
        }
      >
        {/*
          Not `.link-arrow`: that recipe draws a trailing → with ::after, which on a BACK link
          renders "← Back to the queue →". Same colours, arrow supplied by the icon instead.
        */}
        <Link
          to="/support"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-800 transition-colors"
        >
          <Icon name="arrowLeft" className="h-4 w-4" />
          {t('support.ticket.back')}
        </Link>
      </PageHead>

      <PageBody>
        {/*
          Only ever appears while the agent is mid-edit — an idle page has already taken the
          update. Sending is HELD until this is cleared, so the banner has to say so: a
          disabled send button with no stated reason is the version of this that gets read as
          a bug. Taking the update is one deliberate click and never touches the composer.
        */}
        {hasRemoteChanges ? (
          <StatusBanner tone="warning">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>
                {t('support.ticket.remoteChanges', {
                  changes: remoteChanges.join(', '),
                })}
              </span>
              <button onClick={showLatest} className="btn-neutral btn-sm">
                {t('support.ticket.showLatest')}
              </button>
            </div>
          </StatusBanner>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="space-y-3">
              {ticket.messages.map((message) => (
                <div
                  key={message.id}
                  className={`rounded-2xl p-4 border ${
                    message.visibility === 'INTERNAL'
                      ? // Internal notes get a distinct, unmistakable treatment. An agent
                        // skimming a thread must never mistake one for something the
                        // merchant has read — hence the left accent bar, which stays
                        // visible at any scroll position, not just the label at the top.
                        'bg-amber-50 border-amber-200 border-l-4 border-l-amber-500'
                      : message.authorType === 'AGENT'
                        ? 'bg-primary-50/60 border-primary-100'
                        : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3 text-xs">
                    <span className="text-gray-900 font-medium">
                      {message.authorName}
                      <span className="text-gray-500 font-normal">
                        {' '}
                        · {supportEnumLabel(t, 'author', message.authorType)}
                      </span>
                    </span>
                    <span className="text-gray-500 shrink-0">
                      {ts(message.createdAt, locale)}
                    </span>
                  </div>
                  {message.visibility === 'INTERNAL' ? (
                    <div className="text-amber-800 text-xs mt-1 uppercase tracking-wide font-semibold">
                      {t('support.ticket.internalBadge')}
                    </div>
                  ) : null}
                  <p className="text-gray-800 text-sm mt-2 whitespace-pre-wrap">
                    {message.body}
                  </p>
                  {message.attachments.length > 0 ? (
                    <ul className="mt-3 space-y-1 text-xs text-gray-600">
                      {message.attachments.map((attachment) => (
                        <li key={attachment.id}>
                          {/*
                            A button, not a link: the agent route is behind the operator token,
                            so the bytes come through an authenticated request and reach the
                            browser as a blob. A screenshot an agent can see the name of but not
                            open was the sharpest edge left in this feature.
                          */}
                          <button
                            type="button"
                            disabled={downloadingAttachment !== null}
                            onClick={() =>
                              handleAttachmentDownload(
                                attachment.id,
                                attachment.fileName,
                              )
                            }
                            className="inline-flex items-center gap-1.5 text-primary-700 hover:text-primary-800 hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
                          >
                            <Icon name="paperclip" className="h-3.5 w-3.5" />
                            {attachment.fileName} (
                            {t('support.ticket.attachmentSize', {
                              size: Math.round(attachment.sizeBytes / 1024),
                            })}
                            )
                            {downloadingAttachment === attachment.id
                              ? ` — ${t('support.ticket.attachmentDownloading')}`
                              : ''}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ))}
            </div>

            {attachmentError ? (
              <StatusBanner tone="error">
                {t('support.ticket.attachmentError', {
                  fileName: attachmentError,
                })}
              </StatusBanner>
            ) : null}

            <div
              className={`rounded-xl p-4 space-y-3 border ${
                // The composer itself changes colour, because the irreversible mistake is
                // made HERE — a placeholder disappears as soon as the agent starts typing,
                // and a small checkbox above 400 words of reply is not a signal.
                isInternal
                  ? 'bg-amber-50 border-amber-300'
                  : 'bg-white border-gray-200 shadow-sm'
              }`}
            >
              {/*
                Shown only while the restored text is still untouched, so it reads as an
                account of where the words came from rather than a status that lingers over
                something the agent has since rewritten.
              */}
              {restoredDraft && body === restoredDraft.body ? (
                <p className="text-xs text-gray-500">
                  {/*
                    Two keys rather than one with a {{kind}} placeholder: German declines the
                    noun and reorders the sentence around it, so a shared frame would only be
                    translatable into one of the two readings.
                  */}
                  {t(
                    restoredDraft.isInternal
                      ? 'support.ticket.draftRestoredNote'
                      : 'support.ticket.draftRestoredReply',
                    { time: ts(restoredDraft.savedAt, locale) },
                  )}
                </p>
              ) : null}

              <div className="space-y-2">
                <CannedResponsePicker
                  ticket={ticket}
                  onInsert={(text, snippet, language) =>
                    handleInsertSnippet(text, snippet.title, language)
                  }
                />
                {insertedSnippet && body === insertedSnippet.body ? (
                  <p className="text-xs text-gray-500">
                    {t('cannedResponses.picker.inserted', {
                      title: insertedSnippet.title,
                      language: t(
                        insertedSnippet.language === 'de'
                          ? 'cannedResponses.german'
                          : 'cannedResponses.english',
                      ),
                    })}
                  </p>
                ) : null}
              </div>

              <textarea
                value={body}
                onChange={(entry) => setBody(entry.target.value)}
                rows={6}
                lang={draftLanguage(ticket.locale, isInternal, locale)}
                spellCheck
                aria-label={t(
                  isInternal
                    ? 'support.ticket.internalAria'
                    : 'support.ticket.replyAria',
                )}
                placeholder={t(
                  isInternal
                    ? 'support.ticket.internalPlaceholder'
                    : 'support.ticket.replyPlaceholder',
                )}
                className="field-input"
              />
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={isInternal}
                    onChange={(entry) => setIsInternal(entry.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 accent-amber-500"
                  />
                  {t('support.ticket.internalCheckbox')}
                </label>

                {!isInternal ? (
                  <select
                    value={nextStatus}
                    aria-label={t('support.ticket.statusAfterLabel')}
                    onChange={(entry) => setNextStatus(entry.target.value)}
                    className="field-input w-auto"
                  >
                    <option value="">{t('support.ticket.leaveStatus')}</option>
                    {ticket.allowedNextStatuses.map((status) => (
                      <option key={status} value={status}>
                        {t('support.ticket.setStatusTo', {
                          status: supportEnumLabel(t, 'status', status),
                        })}
                      </option>
                    ))}
                  </select>
                ) : null}

                {/*
                  Blocked, not merely warned, while the ticket is known to have moved: a reply
                  answering a question the merchant has since amended, or duplicating one a
                  colleague already sent, cannot be taken back once it leaves. Clearing the
                  block is one click on the banner above and costs the agent nothing — the
                  draft is component state and no code path here touches it.
                */}
                <button
                  disabled={
                    !body.trim() || sendMutation.isPending || hasRemoteChanges
                  }
                  title={
                    hasRemoteChanges
                      ? t('support.ticket.blockedTitle')
                      : undefined
                  }
                  onClick={() => sendMutation.mutate()}
                  className={
                    isInternal
                      ? 'btn-sm ml-auto inline-flex items-center justify-center rounded-lg font-medium bg-amber-500 text-gray-900 hover:bg-amber-400 transition-colors disabled:cursor-not-allowed disabled:opacity-40'
                      : 'btn-primary btn-sm ml-auto'
                  }
                >
                  {t(
                    sendMutation.isPending
                      ? 'support.ticket.sending'
                      : isInternal
                        ? 'support.ticket.addNote'
                        : 'support.ticket.sendReply',
                  )}
                </button>
              </div>
              {isTicketConflict(sendMutation.error) ? (
                <StatusBanner tone="warning">
                  {t('support.ticket.conflict')}
                </StatusBanner>
              ) : sendMutation.isError ? (
                <StatusBanner tone="error">
                  {t('support.ticket.sendError')}
                </StatusBanner>
              ) : null}
            </div>
          </div>

          <div className="space-y-4">
            {/*
              Focus here counts as editing. An open `<select>` holds no state we can see, but
              re-binding its value while the agent is looking at the option list is the same
              interruption as overwriting a typed reply.
            */}
            <div
              className="surface p-4 space-y-3"
              onFocusCapture={() => setPanelFocused(true)}
              onBlurCapture={() => setPanelFocused(false)}
            >
              <div>
                <label className="field-label" htmlFor="ticket-status">
                  {t('support.ticket.statusLabel')}
                </label>
                <select
                  id="ticket-status"
                  value={ticket.status}
                  onChange={(entry) =>
                    statusMutation.mutate(entry.target.value)
                  }
                  disabled={statusMutation.isPending}
                  className="field-input"
                >
                  <option value={ticket.status}>
                    {supportEnumLabel(t, 'status', ticket.status)}
                  </option>
                  {ticket.allowedNextStatuses.map((status) => (
                    <option key={status} value={status}>
                      {supportEnumLabel(t, 'status', status)}
                    </option>
                  ))}
                </select>
                {/*
                  Shown only while RESOLVED is actually reachable, so the checkbox never appears
                  on a ticket it cannot apply to. It is read at the moment the status changes,
                  which is why it sits above the select rather than in a dialog: the select IS
                  the action, and there is no confirm step to hang it off.
                */}
                {ticket.allowedNextStatuses.includes('RESOLVED') && (
                  <label className="mt-2 flex items-start gap-2 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={requestReviewAsk}
                      onChange={(entry) =>
                        setRequestReviewAsk(entry.target.checked)
                      }
                      disabled={statusMutation.isPending}
                      className="mt-0.5 accent-primary"
                    />
                    <span>
                      {t('support.ticket.reviewAsk')}
                      <span className="block text-gray-400">
                        {t('support.ticket.reviewAskHint')}
                      </span>
                    </span>
                  </label>
                )}
              </div>
              <div>
                <label className="field-label" htmlFor="ticket-priority">
                  {t('support.ticket.priorityLabel')}
                </label>
                <select
                  id="ticket-priority"
                  value={ticket.priority}
                  onChange={(entry) =>
                    priorityMutation.mutate(entry.target.value)
                  }
                  disabled={priorityMutation.isPending}
                  className="field-input"
                >
                  {PRIORITIES.map((priority) => (
                    <option key={priority} value={priority}>
                      {supportEnumLabel(t, 'priority', priority)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label" htmlFor="ticket-category">
                  {t('support.ticket.categoryLabel')}
                </label>
                <select
                  id="ticket-category"
                  value={ticket.category}
                  onChange={(entry) =>
                    categoryMutation.mutate(entry.target.value)
                  }
                  disabled={categoryMutation.isPending}
                  className="field-input"
                >
                  {CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {supportEnumLabel(t, 'category', category)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label" htmlFor="ticket-assignee">
                  {t('support.ticket.assigneeLabel')}
                </label>
                <select
                  id="ticket-assignee"
                  value={ticket.assignee ?? ''}
                  onChange={(entry) =>
                    assigneeMutation.mutate(entry.target.value)
                  }
                  disabled={assigneeMutation.isPending}
                  className="field-input"
                >
                  <option value="">{t('support.ticket.unassigned')}</option>
                  {(agentsQuery.data ?? []).map((agent) => (
                    <option key={agent.username} value={agent.username}>
                      {agent.displayName}
                    </option>
                  ))}
                </select>
              </div>
              {categoryMutation.isError ||
              statusMutation.isError ||
              priorityMutation.isError ||
              assigneeMutation.isError ? (
                <StatusBanner tone="error">
                  {t('support.ticket.changeRejected')}
                </StatusBanner>
              ) : null}
            </div>

            <SidePanel title={t('support.ticket.panelTicket')}>
              <dl className="space-y-1.5 text-xs">
                <Row label={t('support.ticket.requester')}>
                  {ticket.requesterEmail}
                </Row>
                <Row label={t('support.ticket.language')}>
                  {formatLanguageName(ticket.locale, locale)}
                </Row>
                <Row label={t('support.ticket.opened')}>
                  {ts(ticket.createdAt, locale)}
                </Row>
                <Row
                  label={t('support.ticket.responseDue')}
                  tone={ticket.overdue ? 'text-red-600 font-medium' : undefined}
                >
                  {ticket.firstResponseDueAt
                    ? ts(ticket.firstResponseDueAt, locale)
                    : '—'}
                </Row>
                <Row label={t('support.ticket.firstAnswered')}>
                  {ticket.firstRespondedAt
                    ? ts(ticket.firstRespondedAt, locale)
                    : t('support.ticket.notYet')}
                </Row>
                {/* Only present once someone asked for the invitation. The row is the answer
                    to "is that still going to happen", which the checkbox — reset after every
                    status change — cannot give. */}
                {askState ? (
                  <Row
                    label={t('support.ticket.reviewAskLabel')}
                    tone={
                      askState.key === 'support.ticket.reviewAskPending'
                        ? 'text-primary-700 font-medium'
                        : undefined
                    }
                  >
                    {askState.time
                      ? t(askState.key, { time: ts(askState.time, locale) })
                      : t(askState.key)}
                  </Row>
                ) : null}
              </dl>
            </SidePanel>

            {/* Frozen at the moment the merchant wrote in, which is what makes "it was
                broken yesterday" diagnosable today. */}
            <SidePanel title={t('support.ticket.panelContext')}>
              <dl className="space-y-1.5 text-xs">
                {Object.entries(ticket.contextSnapshot).map(([key, value]) => (
                  <Row
                    key={key}
                    label={contextLabel(key)}
                    tone={
                      changedKeys.has(key)
                        ? 'text-gray-400 line-through'
                        : undefined
                    }
                  >
                    {snapshotValue(key, value, t, locale)}
                  </Row>
                ))}
              </dl>
            </SidePanel>

            {/*
              The live counterpart. Same keys, built by the same service that wrote the frozen
              one, which is what makes "changed since" a comparison rather than a guess — and
              what stops a merchant's fix ("I corrected the SMTP password an hour ago") reading
              as a contradiction of the ticket.
            */}
            <SidePanel title={t('support.ticket.panelShopNow')}>
              {shopStateQuery.isLoading ? (
                <p className="text-xs text-gray-500">
                  {t('support.ticket.shopNowLoading')}
                </p>
              ) : shopStateQuery.isError ? (
                <p className="text-xs text-gray-500">
                  {t('support.ticket.shopNowError')}
                </p>
              ) : shopState ? (
                <>
                  {shopState.unavailableReason ? (
                    <p className="text-xs text-amber-700 mb-2">
                      {t('support.ticket.shopNowPartial', {
                        reason: shopState.unavailableReason,
                      })}
                    </p>
                  ) : null}
                  <dl className="space-y-1.5 text-xs">
                    {Object.entries(shopState.snapshot)
                      // `capturedAt` is printed once at the foot of the panel, and
                      // `unavailableReason` once at its head — neither is a fact about the
                      // shop, and rendering them as rows here says each of them twice.
                      .filter(
                        ([key]) =>
                          key !== 'capturedAt' && key !== 'unavailableReason',
                      )
                      .map(([key, value]) => (
                        <Row
                          key={key}
                          label={contextLabel(key)}
                          tone={
                            changedKeys.has(key)
                              ? 'text-amber-700 font-medium'
                              : undefined
                          }
                        >
                          {snapshotValue(key, value, t, locale)}
                        </Row>
                      ))}
                    <Row label={t('support.ticket.lastInvoice')}>
                      {shopState.lastInvoiceIssuedAt
                        ? ts(shopState.lastInvoiceIssuedAt, locale)
                        : t('support.ticket.lastInvoiceNone')}
                    </Row>
                    <Row
                      label={t('support.ticket.stuckDocuments')}
                      tone={
                        shopState.failedDocumentCount > 0
                          ? 'text-red-600 font-medium'
                          : undefined
                      }
                    >
                      {shopState.failedDocumentCount}
                    </Row>
                    <Row label={t('support.ticket.lastWebhook')}>
                      {shopState.lastWebhookReceivedAt
                        ? ts(shopState.lastWebhookReceivedAt, locale)
                        : t('support.ticket.lastWebhookNever')}
                    </Row>
                  </dl>

                  {shopState.openAlerts.length > 0 ? (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-gray-500 text-xs mb-1.5">
                        {t('support.ticket.unresolvedAlerts')}
                      </p>
                      <ul className="space-y-1 text-xs">
                        {shopState.openAlerts.map((alert) => (
                          <li key={alert.id} className="text-gray-700">
                            {/* The severity stays the raw enum in both locales. It is an
                                INFRASTRUCTURE token, on the same footing as a job state:
                                the ticket's own enums are translated, an alert's is not. */}
                            <span
                              className={
                                alert.type === 'ERROR'
                                  ? 'text-red-600 font-medium'
                                  : 'text-amber-700'
                              }
                            >
                              {alert.type}
                            </span>{' '}
                            {alert.title}
                            <span className="block text-gray-400">
                              {ts(alert.createdAt, locale)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  <p className="mt-3 text-xs text-gray-400">
                    {t('support.ticket.shopNowReadAt', {
                      time: ts(shopState.capturedAt, locale),
                    })}
                  </p>
                </>
              ) : null}
            </SidePanel>

            {/* The ticket rides along into the shop file, so its reason prompt opens with ONE
                click (the ticket is the Anlass) instead of asking the agent to type why. */}
            <SidePanel title={t('support.ticket.panelDocuments')}>
              <Link
                to={shopFilePath(ticket.shopId, '', id)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-700 hover:text-primary-800"
              >
                <Icon name="folder" className="h-4 w-4" />
                {t('support.ticket.shopFileLink')}
              </Link>
              <p className="mt-3 text-xs text-gray-600">
                {t('support.ticket.documentsLead')}
              </p>
              {/* Carries the shop through, so the lookup reads a number the way THIS shop's
                  documents are numbered rather than guessing at the prefix. */}
              <Link
                to={documentLookupPath(ticket.shopId)}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-primary-700 hover:text-primary-800"
              >
                <Icon name="receipt" className="h-4 w-4" />
                {t('support.ticket.documentsSearch')}
              </Link>
              <Link
                to={shopFilePath(ticket.shopId, 'numbering', id)}
                className="mt-2 flex items-center gap-1.5 text-xs font-medium text-primary-700 hover:text-primary-800"
              >
                <Icon name="hash" className="h-4 w-4" />
                {t('support.ticket.numberingLink')}
              </Link>
            </SidePanel>

            <SidePanel title={t('support.ticket.panelHistory')}>
              <ul className="space-y-1.5 text-xs text-gray-600">
                {ticket.events.map((event) => {
                  // Which enum the two sides of a transition are drawn from depends on the
                  // event: an ASSIGNED carries a username, and translating that would be
                  // nonsense — hence `null` for those and the raw value printed as it stands.
                  const valueGroup = supportEventValueGroup(event.type);
                  const sideLabel = (value: string | null | undefined) =>
                    value
                      ? valueGroup
                        ? supportEnumLabel(t, valueGroup, value)
                        : value
                      : '—';
                  // Both formatters are injected rather than reached for inside the helper, so
                  // the branch that decides WHICH shape an entry has stays one pure function
                  // while the copy stays here with the rest of the page's i18n.
                  const detail = historyDetail(
                    event,
                    (iso) => ts(iso, locale),
                    sideLabel,
                  );
                  // The cross-check the raw tokens existed for, kept where it actually
                  // happens. The event TYPE was already one hover away; the two sides are
                  // translated as well — and now so is a review invitation's drop reason, so
                  // `asked-recently` would otherwise appear nowhere on the page an operator
                  // could match against the worker log, which logs that exact string.
                  const rawTokens = [event.type, event.fromValue, event.toValue]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <li key={event.id} title={rawTokens || undefined}>
                      <span className="text-gray-900 font-medium">
                        {supportEnumLabel(t, 'event', event.type) || '—'}
                      </span>
                      {detail ? <span> {detail}</span> : null}
                      {event.actorRef ? <span> · {event.actorRef}</span> : null}
                      <div className="text-gray-400">
                        {ts(event.createdAt, locale)}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </SidePanel>
          </div>
        </div>
      </PageBody>
    </>
  );
}
